//src/services/openepdservice.js
const PROXY_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "/api/openepd"
    : process.env.REACT_APP_OPENEPD_PROXY_TARGET || "https://embodix.vercel.app/api/openepd";

const RESOURCE_CACHE = new Map();
let CATEGORIES_CACHE = null;
let PROXY_META_CACHE = null;

function keyOf(kind, id) {
  return `${String(kind || "product")}:${String(id || "")}`;
}

async function promisePool(items, worker, concurrency = 4) {
  const list = Array.isArray(items) ? items : [];
  const results = new Array(list.length);

  let idx = 0;
  const runners = new Array(Math.max(1, concurrency)).fill(0).map(async () => {
    while (idx < list.length) {
      const myIdx = idx;
      idx += 1;
      results[myIdx] = await worker(list[myIdx], myIdx);
    }
  });

  await Promise.all(runners);
  return results;
}

function mergeBuckets(buckets, kind) {
  const seen = new Map();
  let total = 0;

  for (const b of buckets || []) {
    if (!b) continue;
    total += Number(b.totalCount || 0);
    for (const it of b.items || []) {
      const k = keyOf(kind, it?.id);
      if (!seen.has(k) && it?.id) seen.set(k, it);
    }
  }

  return {
    items: Array.from(seen.values()),
    totalCount: total
  };
}

function buildOmf(term) {
  const t = safeString(term).trim();
  if (!t) return "";
  const escaped = t.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `!EC3 search("${escaped}") !pragma oMF("1.0/1")`;
}

function toCacheKey(method, path, body) {
  if (method === "GET") return `GET:${path}`;
  return `${method}:${path}:${body ? JSON.stringify(body) : ""}`;
}

async function fetchJSON(path, { signal, method = "GET", body } = {}) {
  const cacheKey = toCacheKey(method, path, body);
  if (!signal && method === "GET" && RESOURCE_CACHE.has(cacheKey)) return RESOURCE_CACHE.get(cacheKey);

  const url = `${PROXY_BASE_URL}?path=${encodeURIComponent(path)}`;

  const maxRetries = 2;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (signal?.aborted) {
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const response = await fetch(url, {
      method,
      headers: { Accept: "application/json", ...(method === "POST" ? { "Content-Type": "application/json" } : {}) },
      body: method === "POST" ? JSON.stringify(body || {}) : undefined,
      signal
    });

    if (response.ok) {
      const data = await response.json();
      if (!signal && method === "GET") RESOURCE_CACHE.set(cacheKey, data);
      return data;
    }

    const status = response.status;
    const retryAfterHeader = response.headers.get("Retry-After");

    if ((status === 429 || status === 503) && attempt < maxRetries) {
      const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;
      const waitMs = Number.isFinite(retryAfterSeconds)
        ? Math.max(500, retryAfterSeconds * 1000)
        : 800 * (attempt + 1);

      await new Promise((r) => setTimeout(r, waitMs));
      continue;
    }

    let details = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text();
    }

    let detailsText = "";
    if (typeof details === "string") detailsText = details;
    else if (details && typeof details === "object") detailsText = JSON.stringify(details);

    const err = new Error(`OpenEPD request failed (${status}) ${detailsText}`.trim());
    err.status = status;
    err.retryAfter = retryAfterHeader;
    throw err;
  }
}

function safeString(v) {
  return v == null ? "" : String(v);
}

function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function guessCategoryId(input) {
  const s = safeString(input).trim();
  if (!s) return "";
  const cleaned = s.replace(/[^A-Za-z0-9 ]+/g, " ").trim();
  if (!cleaned) return "";
  return cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

export async function fetchOpenEpdCategoriesTree({ signal, force = false } = {}) {
  if (!force && CATEGORIES_CACHE) return CATEGORIES_CACHE;
  const raw = await fetchJSON("v2/categories/tree", { signal });
  const payload = raw?.payload ?? raw;
  if (payload) CATEGORIES_CACHE = payload;
  return payload;
}

function normalizePagedResponse(raw) {
  const root = raw?.payload ?? raw ?? {};
  const meta = root?.meta || root?.paging || root?.page || root?.data?.meta || null;

  const data =
    root?.data ||
    root?.results ||
    root?.items ||
    root?.epds ||
    root?.generic_estimates ||
    root?.industry_epds ||
    root ||
    [];

  const list = Array.isArray(data) ? data : Array.isArray(root?.data) ? root.data : [];

  const totalCount = Number(
    meta?.total_count ?? meta?.total ?? meta?.count ?? root?.total_count ?? root?.total ?? root?.count ?? list.length
  );
  const pageNumber = Number(meta?.page_number ?? meta?.page ?? root?.page_number ?? root?.page ?? 1);
  const pageSize = Number(meta?.page_size ?? root?.page_size ?? list.length);

  return {
    results: list,
    totalCount: Number.isFinite(totalCount) ? totalCount : list.length,
    pageNumber: Number.isFinite(pageNumber) ? pageNumber : 1,
    pageSize: Number.isFinite(pageSize) ? pageSize : list.length
  };
}

function pickCountry(raw) {
  const c =
    raw?.plant?.location?.country ||
    raw?.plant?.country ||
    raw?.org?.address?.country ||
    raw?.org?.country ||
    raw?.country ||
    "";
  return safeString(c).trim() || null;
}

function pickLocationLabel(raw) {
  const loc = raw?.plant?.location || raw?.plant || raw?.location || null;
  if (!loc) return null;
  const parts = [];
  const city = safeString(loc.city || loc.locality || "").trim();
  const region = safeString(loc.region || loc.state || "").trim();
  const country = safeString(loc.country || "").trim();
  if (city) parts.push(city);
  if (region) parts.push(region);
  if (country) parts.push(country);
  return parts.length ? parts.join(", ") : null;
}

function pickDeclaredUnit(raw) {
  const du = raw?.declared_unit || raw?.declaredUnit || raw?.declared_unit_name || null;
  if (typeof du === "string") return du;
  if (isObj(du)) return du.unit || du.name || du.label || null;
  return null;
}

function pickManufacturer(raw) {
  return safeString(raw?.org?.name || raw?.manufacturer || raw?.company || raw?.company_name || "").trim() || null;
}

function pickDisplayName(raw) {
  return safeString(raw?.product_name || raw?.name || raw?.title || raw?.display_name || "Unnamed").trim();
}

function pickValidUntil(raw) {
  return raw?.valid_until || raw?.validUntil || raw?.expires_at || null;
}

export function extractListExtrasFromRecord(raw) {
  const manufacturer = pickManufacturer(raw);
  const country = pickCountry(raw);
  const locationLabel = pickLocationLabel(raw);
  const declaredUnit = pickDeclaredUnit(raw);
  const validUntil = pickValidUntil(raw);

  return {
    manufacturer,
    country,
    locationLabel,
    declaredUnit,
    validUntil
  };
}

export async function fetchOpenEpdListItemExtras({ id, kind = "product", signal } = {}) {
  if (!id) throw new Error("Missing OpenEPD id");
  const fields = ["org", "plant", "declared_unit", "valid_until"];
  const record = await getOpenEpdRecordById({ id, kind, fields, signal });
  return extractListExtrasFromRecord(record?.payload ?? record);
}

export function normalizeOpenEpdListItem(raw, { kind }) {
  const id = raw?.id || raw?.open_xpd_uuid || raw?.open_epd_uuid || raw?.uuid || null;

  return {
    kind,
    id: id ? String(id) : null,
    displayName: pickDisplayName(raw),
    manufacturer: pickManufacturer(raw),
    locationLabel: pickLocationLabel(raw),
    country: pickCountry(raw),
    declaredUnit: pickDeclaredUnit(raw),
    validUntil: pickValidUntil(raw),
    raw
  };
}

export function listImpactMethods(epd) {
  const impacts = epd?.impacts;
  if (!isObj(impacts)) return [];
  return Object.keys(impacts);
}

export function listImpactKeys(epd, methodKey) {
  const node = epd?.impacts?.[methodKey];
  if (!isObj(node)) return [];
  return Object.keys(node);
}

const STAGE_ORDER = [
  "A1",
  "A2",
  "A3",
  "A1A2A3",
  "A1A2A3A4",
  "A4",
  "A5",
  "B1",
  "B2",
  "B3",
  "B4",
  "B5",
  "B6",
  "B7",
  "C1",
  "C2",
  "C3",
  "C4",
  "D"
];

function stageLabel(stage) {
  if (stage === "A1A2A3") return "A1-A3";
  if (stage === "A1A2A3A4") return "A1-A4";
  return stage;
}

export function extractImpactRows(epd, methodKey, impactKey) {
  const node = epd?.impacts?.[methodKey]?.[impactKey];
  if (!isObj(node)) return { unit: null, rows: [] };

  const rows = [];
  for (const stage of STAGE_ORDER) {
    const v = node?.[stage];
    if (!isObj(v)) continue;
    const mean = v.mean ?? null;
    const unit = v.unit ?? null;
    const rsd = v.rsd ?? null;
    rows.push({ stage, stageLabel: stageLabel(stage), mean, unit, rsd });
  }

  const unit = rows.find((r) => r.unit)?.unit || null;
  return { unit, rows };
}

export function pickBestMethod(epd) {
  const methods = listImpactMethods(epd);
  if (!methods.length) return null;
  const preferred = ["TRACI", "EF", "IPCC"];
  for (const p of preferred) {
    const hit = methods.find((m) => safeString(m).toLowerCase().includes(p.toLowerCase()));
    if (hit) return hit;
  }
  return methods[0];
}

export async function searchOpenEpdAll({
  searchTerm,
  categoryTerm,
  categoryIds,
  pageNumber = 1,
  pageSize = 50,
  includeGeneric = true,
  includeIndustry = true,
  signal
} = {}) {
  const makeEmpty = () => ({ items: [], totalCount: 0, pageNumber, pageSize });

  const leafIds = Array.isArray(categoryIds) ? categoryIds.filter(Boolean).map(String) : [];

  const rawInput = safeString(searchTerm ?? categoryTerm).trim();
  const guessed = guessCategoryId(rawInput);

  const omfs =
    leafIds.length > 0
      ? leafIds.map((id) => buildOmf(id)).filter(Boolean)
      : [buildOmf(guessed || rawInput)].filter(Boolean);

  if (!omfs.length) {
    return { omfs: [], product: makeEmpty(), generic: makeEmpty(), industry: makeEmpty() };
  }

  const fields = ["id", "product_name", "name", "declared_unit", "org", "plant", "valid_until"].join(",");
  const buildPath = (resource, omfValue) =>
    `${resource}?omf=${omfValue}&page_number=${pageNumber}&page_size=${pageSize}&fields=${fields}`;

  const toBucket = (meta, kind) => ({
    items: meta.results.map((r) => normalizeOpenEpdListItem(r, { kind })),
    totalCount: meta.totalCount,
    pageNumber: meta.pageNumber,
    pageSize: meta.pageSize
  });

  const fetchList = async (resource, kind, omfValue) => {
    const raw = await fetchJSON(buildPath(resource, omfValue), { signal });
    const meta = normalizePagedResponse(raw);
    return toBucket(meta, kind);
  };

  const productBuckets = await promisePool(
    omfs,
    async (omf) => fetchList("v2/epds/search", "product", omf),
    3
  );

  const mergedProduct = mergeBuckets(productBuckets, "product");

  const genericBuckets = includeGeneric
    ? await promisePool(
        omfs,
        async (omf) => fetchList("v2/generic_estimates/search", "generic", omf).catch(() => makeEmpty()),
        3
      )
    : [];

  const industryBuckets = includeIndustry
    ? await promisePool(
        omfs,
        async (omf) => fetchList("v2/industry_epds/search", "industry", omf).catch(() => makeEmpty()),
        3
      )
    : [];

  const mergedGeneric = includeGeneric ? mergeBuckets(genericBuckets, "generic") : makeEmpty();
  const mergedIndustry = includeIndustry ? mergeBuckets(industryBuckets, "industry") : makeEmpty();

  return {
    omfs,
    product: {
      ...makeEmpty(),
      items: mergedProduct.items,
      totalCount: mergedProduct.totalCount
    },
    generic: {
      ...makeEmpty(),
      items: mergedGeneric.items,
      totalCount: mergedGeneric.totalCount
    },
    industry: {
      ...makeEmpty(),
      items: mergedIndustry.items,
      totalCount: mergedIndustry.totalCount
    }
  };
}

export async function getOpenEpdById({ id, fields, signal } = {}) {
  if (!id) throw new Error("Missing OpenEPD id");
  const f = Array.isArray(fields) && fields.length ? `?fields=${encodeURIComponent(fields.join(","))}` : "";
  const path = `epds/${encodeURIComponent(String(id))}${f}`;
  return fetchJSON(path, { signal });
}

export async function getOpenEpdRecordById({ id, kind = "product", fields, signal } = {}) {
  if (!id) throw new Error("Missing OpenEPD id");

  const f = Array.isArray(fields) && fields.length ? `?fields=${encodeURIComponent(fields.join(","))}` : "";

  if (kind === "generic") {
    return fetchJSON(`v2/generic_estimates/${encodeURIComponent(String(id))}${f}`, { signal });
  }

  if (kind === "industry") {
    return fetchJSON(`v2/industry_epds/${encodeURIComponent(String(id))}${f}`, { signal });
  }

  return fetchJSON(`epds/${encodeURIComponent(String(id))}${f}`, { signal });
}

export async function getOpenEpdStatistics({ omf, signal } = {}) {
  if (!omf) throw new Error("Missing omf");
  const path = `v2/epds/statistics?omf=${omf}`;
  return fetchJSON(path, { signal });
}

export async function fetchOpenEpdProxyMeta({ signal, force = false } = {}) {
  if (!force && PROXY_META_CACHE) return PROXY_META_CACHE;
  const meta = await fetchJSON("__meta", { signal });
  if (meta) PROXY_META_CACHE = meta;
  return meta;
}

export function refineClientSide(items, { manufacturer, country, declaredUnit, kind, nameContains } = {}) {
  const m = safeString(manufacturer).trim().toLowerCase();
  const c = safeString(country).trim().toUpperCase();
  const u = safeString(declaredUnit).trim().toLowerCase();
  const k = safeString(kind).trim().toLowerCase();
  const n = safeString(nameContains).trim().toLowerCase();

  return (items || []).filter((it) => {
    if (k && safeString(it.kind).toLowerCase() !== k) return false;

    if (m) {
      const hay = safeString(it.manufacturer).toLowerCase();
      if (!hay.includes(m)) return false;
    }

    if (c) {
      const cc = safeString(it.country).toUpperCase();
      if (cc !== c) return false;
    }

    if (u) {
      const uu = safeString(it.declaredUnit).toLowerCase();
      if (uu !== u) return false;
    }

    if (n) {
      const dn = safeString(it.displayName).toLowerCase();
      if (!dn.includes(n)) return false;
    }

    return true;
  });
}
